import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Edit3, X, Copy, RefreshCw, ExternalLink } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function ProgramsPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/programs").then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/users").then(setUsers).catch(() => setUsers([]));
  }, []);

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      Object.keys(body).forEach((k) => { if (body[k] === undefined) delete body[k]; });
      if (editing.id && items.find((x) => x.id === editing.id)) {
        await api.put(`/admin/programs/${editing.id}`, body);
      } else {
        delete body.id;
        await api.post("/admin/programs", body);
      }
      setEditing(null); load();
    } catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar programa?")) return;
    await api.delete(`/admin/programs/${id}`); load();
  };

  const regenPin = async (id) => {
    const r = await api.post(`/admin/programs/${id}/regen-pin`, {});
    alert(`Nuevo PIN: ${r.pin}`); load();
  };

  const portalUrl = (p) => `${window.location.origin}/produccion/${p.slug}?pin=${p.pin}`;

  return (
    <div data-testid="commercial-programs">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Programas de Streaming</h1>
          <p className="text-sm text-muted-foreground">Contenedores donde se ejecutan campañas y acciones · Total: {items.length}</p>
        </div>
        <button onClick={() => setEditing({ name: "", schedule: "", description: "", is_active: true, hosts: [] })} data-testid="program-new" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nuevo programa
        </button>
      </div>

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6 bg-secondary/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id ? "Editar programa" : "Nuevo programa"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nombre" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} span={2} />
            <Field label="Horario" value={editing.schedule} placeholder="Lun-Vie 7-9" onChange={(v) => setEditing({ ...editing, schedule: v })} />
            <Field label="Categoría" value={editing.category} placeholder="Magazine, Deportivo, ..." onChange={(v) => setEditing({ ...editing, category: v })} />
            <Field label="Audiencia estimada" value={editing.audience_estimate} placeholder="50.000 viewers/día" onChange={(v) => setEditing({ ...editing, audience_estimate: v })} />
            <Field label="Público objetivo" value={editing.target_audience} placeholder="18-45 años, NSE B/C" onChange={(v) => setEditing({ ...editing, target_audience: v })} />
            <Field label="Logo (URL)" value={editing.logo} onChange={(v) => setEditing({ ...editing, logo: v })} />
            <Field label="Cover (URL)" value={editing.cover} onChange={(v) => setEditing({ ...editing, cover: v })} />
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Descripción</label>
              <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Productor responsable</label>
              <select value={editing.producer_user_id || ""} onChange={(e) => setEditing({ ...editing, producer_user_id: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
                <option value="">— Ninguno —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 h-10 mt-6">
              <input type="checkbox" checked={editing.is_active !== false} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-primary" />
              <span className="text-sm">Activo</span>
            </label>
          </div>
          {error && <p className="text-sm text-destructive mt-3">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Programa</th>
              <th className="text-left p-3">Horario</th>
              <th className="text-left p-3">Categoría</th>
              <th className="text-left p-3">Productor</th>
              <th className="text-left p-3">Portal</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-secondary/30">
                <td className="p-3 font-semibold">{p.name}<br/><code className="text-xs text-muted-foreground">{p.slug}</code></td>
                <td className="p-3">{p.schedule || "—"}</td>
                <td className="p-3">{p.category || "—"}</td>
                <td className="p-3 text-xs">{users.find((u) => u.id === p.producer_user_id)?.name || users.find((u) => u.id === p.producer_user_id)?.email || "—"}</td>
                <td className="p-3 text-xs">
                  <div className="flex items-center gap-1">
                    <code className="text-[10px] bg-secondary/40 px-1.5 py-0.5 rounded">PIN {p.pin}</code>
                    <button onClick={() => regenPin(p.id)} title="Regenerar PIN" className="p-1 hover:text-primary"><RefreshCw className="w-3 h-3" /></button>
                    <button onClick={() => navigator.clipboard.writeText(portalUrl(p))} title="Copiar link" className="p-1 hover:text-primary"><Copy className="w-3 h-3" /></button>
                    <a href={portalUrl(p)} target="_blank" rel="noreferrer" className="p-1 hover:text-primary"><ExternalLink className="w-3 h-3" /></a>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${p.is_active ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{p.is_active ? "Activo" : "Inactivo"}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...p })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Edit3 className="w-3 h-3" /> Editar</button>
                  <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /> Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Sin programas todavía</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, span }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{label}</label>
      <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
    </div>
  );
}
